/**
 * set on slide change callback for Slider
 * @param {Slider} slider instance of Slider class
 * @param {Function} callback function to call on slide change, it pass 1 argument - slide index
 */
export function setOnSlideChange(slider, callback) {
  const { $sliderFrame, $sliderItems } = slider;
  const $firstSlide = $sliderItems[0];
  let lastIndex = null;

  function onScroll() {
    // Get index number of the most visible slide
    const visibleSlideIndex = Math.round(
      $sliderFrame.scrollLeft / $firstSlide.offsetWidth,
    );

    if (visibleSlideIndex === lastIndex) return; // prevents unnecessary update

    lastIndex = visibleSlideIndex;
    callback(visibleSlideIndex);
  }

  $sliderFrame.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

/**
 * get click handler function that scroll Slider to slide by hash on button
 * @param {Slider} slider instance of Slider class
 * @returns {Function} onNavButtonClick function
 */
export function getOnNavButtonClick(slider) {
  const { $sliderFrame } = slider;

  function onNavButtonClick(e) {
    const $button = e.currentTarget;
    e.preventDefault();

    const hash = $button.getAttribute('href');
    if (hash === null) return;

    const $target = document.getElementById(hash.substring(1));
    if ($target === null) return;

    $sliderFrame.scroll({
      left: $target.offsetLeft,
      behavior: 'smooth',
    });
  }

  return onNavButtonClick;
}

/**
 * set attributes on element
 * @param {Element} $el element
 * @param {Object} attrsObj object of attributes { attrName: AttrValue }, undefined to remove attr
 */
export function setAttrs($el, attrsObj) {
  Object.keys(attrsObj).forEach((attrKey) => {
    const attrValue = attrsObj[attrKey];

    if (attrValue !== undefined) {
      $el.setAttribute(attrKey, attrsObj[attrKey]);
    } else {
      $el.removeAttribute(attrKey);
    }
  });
}

/**
 * add or remove class on element based on condition
 * @param {Element} $el element
 * @param {String} className class name to add or remove
 * @param {Boolean} condition if true add else remove
 */
export function setClassName($el, className, condition = true) {
  if (condition) {
    $el.classList.add(className);
  } else {
    $el.classList.remove(className);
  }
}

export class Pagination {
  constructor($el, slider, options) {
    this.$el = $el;
    this.slider = slider;

    this.id = options.id;
    this.loop = options.loop || ($el.dataset.loop || '').toLowerCase() === 'true';

    this.initPagination(options);
  }

  initPagination(options) {
    const $pagination = this.$el.querySelector(`.${options.pagination || 'pagination'}`);
    this.$navButtonPrev = $pagination.querySelector('.pagination__item--prev a');
    this.$navButtonNext = $pagination.querySelector('.pagination__item--next a');

    const $paginationSummary = $pagination.querySelector('.pagination__summary');
    [this.$pageCurrent] = $paginationSummary.children[0].childNodes;
    [this.$pageTotal] = $paginationSummary.children[2].childNodes;

    this.$pagination = $pagination;

    this.updateTotal();
    this.bindEvents();
  }

  updateTotal() {
    const { $sliderFrame, $sliderItems } = this.slider;

    const itemsInView = Math.floor($sliderFrame.offsetWidth / ($sliderItems[0].offsetWidth - 0.5));
    const pages = $sliderItems.length - (itemsInView - 1);

    this.maxIndex = pages - 1;
    this.$pageTotal.textContent = pages;

    setClassName(this.$pagination, 'pagination--hidden', pages === 1);
  }

  bindEvents() {
    setOnSlideChange(this.slider, this.updatePagination.bind(this));
    const onNavButtonClick = getOnNavButtonClick(this.slider);

    this.$navButtonPrev.addEventListener('click', onNavButtonClick);
    this.$navButtonNext.addEventListener('click', onNavButtonClick);
  }

  updatePagination(index) {
    const { prev, next } = Pagination.getButtonAttrs(index, this.maxIndex, this.loop);

    this.setNavButtonAttrs(this.$navButtonPrev, prev);
    this.setNavButtonAttrs(this.$navButtonNext, next);

    this.$pageCurrent.textContent = index + 1;
  }

  setNavButtonAttrs($navButton, { index, disabled }) {
    setAttrs($navButton, {
      href: disabled ? undefined : `#${this.id}-slide${index}`,
      disabled: disabled ? 'disabled' : undefined,
      'aria-disabled': disabled ? 'true' : undefined,
    });
  }

  static getButtonAttrs(index, maxIndex, loop = true) {
    return {
      prev: {
        index: index === 0 ? maxIndex : index - 1,
        disabled: !loop && index === 0,
      },
      next: {
        index: index === maxIndex ? 0 : index + 1,
        disabled: !loop && index === maxIndex,
      },
    };
  }
}

export class Thumbnails {
  constructor($el, slider, options) {
    this.$el = $el;
    this.slider = slider;

    this.id = options.id;

    this.classes = {
      activeItem: options.activeThumbnail || 'active',
    };

    this.initThumbnails(options);
  }

  initThumbnails(options) {
    const $thumbnails = this.$el.querySelector(`.${options.thumbnails || 'thumbnails'}`);
    this.$thumbnailsFrame = $thumbnails;
    this.$thumbnailsItems = $thumbnails.childNodes;

    this.bindEvents();
  }

  bindEvents() {
    setOnSlideChange(this.slider, this.updateActive.bind(this));
    const onThumbnailClick = getOnNavButtonClick(this.slider);

    this.$thumbnailsItems.forEach(($slide, index) => {
      $slide.setAttribute('href', `#${this.id}-slide${index}`);
      $slide.addEventListener('click', onThumbnailClick);
    });
  }

  updateActive(activeIndex) {
    let $target = null;

    this.$thumbnailsItems.forEach(($slide, index) => {
      if (index === activeIndex) {
        $target = $slide;
        $slide.classList.add(this.classes.activeItem);
      } else {
        $slide.classList.remove(this.classes.activeItem);
      }
    });

    if ($target === null) return;

    this.$thumbnailsFrame.scroll({
      left: $target.offsetLeft,
      behavior: 'smooth',
    });
  }
}

export default {
  setOnSlideChange,
  getOnNavButtonClick,
  Pagination,
  Thumbnails,
};
